<template>
  <div id="tac-admin-payment-reconciliation-batch-summary" class="batch-summary">
    <p class="text-center" v-if="!batchNumber">Find transactions by batch to reconcile.</p>
    <div class="table-responsive" v-if="batchNumber">
      <table class="trx-group-table table table-bordered table-condensed table-compact">
        <thead>
          <tr>
            <th class="col-md-1 bg-light-gray post"></th>
            <th class="col-md-3 bg-light-gray title"></th>
            <th class="col-md-1 bg-light-gray text-right amount">ADMIN$</th>
            <th class="col-md-1 bg-light-gray text-right count">ADMIN#</th>
            <th class="col-md-1 bg-light-gray record text-right">
              <div class="btn-group">
                <button class="btn btn-default btn-xs" type="button" @click="toggleVisibility()" v-show="!visible" v-tooltip title="Hide summary"><span class="glyphicon glyphicon-chevron-up"></span></button>
                <button class="btn btn-default btn-xs" type="button" @click="toggleVisibility()" v-show="visible" v-tooltip title="Show summary"><span class="glyphicon glyphicon-chevron-down"></span></button>
              </div>
            </th>
            <th class="col-md-5 pending"></th>
          </tr>
        </thead>
        <tbody v-show="!visible">
          <template v-for="(group, groupKey) in summary">
          <tr class="group-header table-row-hidden" :key="`${groupKey}-header`">
            <td colspan="6"></td>
          </tr>
          <tr class="group-entry" v-for="(row, rowKey) in group" :key="`${groupKey}-row-${rowKey}`">
            <th class="col-md-1 post text-center" :class="{'bg-info': !!row.can_reconcile && !!row.amount}" v-show="!isRowInCollapsedGroup(row)">
              <div>
                <a class="btn btn-primary btn-xs"
                  :class="{'btn-danger': row.has_reconcile && !row.all_reconciled, 'disabled': isBusy}"
                  v-if="row.count && row.can_reconcile && !row.all_reconciled"
                  @click="reconcile(row.group)"
                  >POST</a>
                <span v-if="!!row.count && !!row.can_reconcile && !!row.all_reconciled">POSTED</span>
              </div>
            </th>
            <th class="col-md-3 title" :class="{'all-cc-total': row.group === 'VMCDAX', 'bg-info': !!row.can_reconcile && !!row.amount}" v-show="!isRowInCollapsedGroup(row)">
              <div class="row">
                <div :class="{'col-md-7': row.group === 'VMCDAX', 'col-md-12': row.group !== 'VMCDAX'}">{{ row.label }}</div>
                <div class="col-md-5" v-if="row.group === 'VMCDAX'">
                  <input type="text" class="form-control text-right input-xs" v-model="findAmount" v-number-format="{style: 'decimal', 'minimumFractionDigits': 2}" :disabled="isBusy" />
                </div>
              </div>
            </th>
            <td class="col-md-1 amount text-right" :class="{'bg-info': !!row.can_reconcile && !!row.amount}" v-show="!isRowInCollapsedGroup(row)"><span><money currencySign="" :value="row.amount"></money></span></td>
            <td class="col-md-1 count text-right" :class="{'bg-info': !!row.can_reconcile && !!row.amount}" v-show="!isRowInCollapsedGroup(row)"><span>{{ row.count }}</span></td>
            <td class="col-md-1 record text-center" :class="{'bg-info': !!row.can_reconcile && !!row.amount}" v-show="!isRowInCollapsedGroup(row)">
              <a class="btn btn-primary btn-xs text-left"
                v-if="!!row.count && row.can_reconcile && row.has_reconcile"
                :class="{disabled: isBusy}"
                @click="details(row.group)"
                >Details</a>
              <span class="btn-toggler" v-if="row.is_collapsible">
                <button class="btn btn-default btn-xs" @click="collapseRow(row)" v-if="!isRowCollapsed(row)"><span class="glyphicon glyphicon-minus"></span></button>
                <button class="btn btn-default btn-xs" @click="expandRow(row)" v-if="isRowCollapsed(row)"><span class="glyphicon glyphicon-plus"></span></button>
              </span>
            </td>
            <td class="col-md-5 pending" :class="{'bg-info': row.group === 'VMCDAX' && pendingGroup(row.group)}" v-show="!isRowInCollapsedGroup(row)">
              <div v-if="row.group === 'VMCDAX' && pendingGroup(row.group)">
                <section>
                  <label class="text-danger" v-if="!reconcileIsComplete(row.group)">Not Reconciled</label>
                  <label class="text-success" v-if="reconcileIsComplete(row.group)">Reconciled</label>
                </section>
                <section>
                  <label>User:</label>
                  <span>{{ pendingGroupProvider(row.group) | toUpper }}</span>
                </section>
                <section>
                  <label>Amount:</label>
                  <span><money currencySign="" :value="pendingGroupStatementAmount(row.group)"></money></span>
                </section>
                <section>
                  <label>Amount:</label>
                  <span>{{ pendingGroupLastModification(row.group) | format('MM/DD/YY') }}</span>
                </section>
              </div>
            </td>
          </tr>
          <tr class="group-footer table-row-hidden" :key="`${groupKey}-footer`">
            <td colspan="6"></td>
          </tr>
          </template>
        </tbody>
      </table><!-- .trx-group-table -->
    </div><!-- .table-responsive -->
  </div><!-- #tac-admin-payment-reconciliation-batch-summary -->
</template>

<script>
  import _ from 'lodash'
  import { mapState, mapGetters } from 'vuex'
  import { toUpper } from '@/plugin/filter/string'
  import { format } from '@/plugin/filter/date'
  import BatchMixin from '../../mixin/batch'
  import MixinLocalStorage from '@/plugin/mixin/local-storage'
  import Money from '@/plugin/element/money'
  import NumberFormat from '@/plugin/directive/number-format'
  import Tooltip from '@/plugin/directive/tooltip'

  const VISIBILITY_STORAGE_NAME = 'TAC.ADMIN.PAYMENT-RECONCILIATION.VISIBILITY'
  const COLLAPSED_ROWS_STORAGE_NAME = 'TAC.ADMIN.PAYMENT-RECONCILIATION.COLLAPSED_ROWS'

  export default {
    name: 'summary-component',
    data() {
      return {
        visible: false,
        collapsedRowIds: []
      }
    },
    created() {
      this.restoreVisibility()
      this.restoreCollapsedRows()
    },
    destroyed() {
      this.$store.dispatch('paymentReconciliation/amountFinder/clear')
    },
    watch: {
      batchNumber() {
        this.findAmount = 0
      }
    },
    computed: {
      ...mapState('paymentReconciliation/batch', {
        batchNumber: 'batchNumber',
        fetching: 'fetching'
      }),
      ...mapGetters('paymentReconciliation/batch', {
        summary: 'tableData',
        pending: 'pendingByGroup',
        transactions: 'transactions'
      }),
      ...mapGetters('paymentReconciliation/amountFinder', {
        started: 'started'
      }),
      findAmount: {
        set(val) {
          if (val) {
            this.$store.dispatch('paymentReconciliation/amountFinder/start', parseFloat(val))
          } else {
            this.$store.dispatch('paymentReconciliation/amountFinder/clear')
          }
        },
        get() {
          return this.$store.getters['paymentReconciliation/amountFinder/amount']
        }
      },
      collapsedRows() {
        return _
          .chain(this.collapsedRowIds)
          .map((collapsedRowId) => {
            return _.find(this.rows, {id: collapsedRowId}) || []
          })
          .filter((collapsedRow) => {
            return undefined !== collapsedRow
          })
          .value()
      },
      rows() {
        let rows = []
        for (const group of this.summary) {
          for (const row of group) {
            rows.push(row)
          }
        }

        return rows
      },
      isBusy() {
        return this.fetching
      }
    },
    methods: {
      toggleVisibility() {
        this.visible = !this.visible
        this.storeVisibility()
      },
      reconcile(group) {
        if (this.isBusy) {
          return
        }

        this.$router.push({
          name: 'admin/payment-reconcile/reconcile',
          params: {
            batchNumber: this.batchNumber,
            group: group
          }
        })
      },
      details(group) {
        if (!this.batchNumber) {
          return
        }

        let params = {
          batchNumber: this.batchNumber
        }

        if (group) {
          params['group'] = group
        }

        this.$router.push({
          name: 'admin/payment-reconcile/record',
          params: params
        })
      },
      reconcileIsComplete(group) {
        const record = _.get(this.pending, group)
        if (!record) {
          return false;
        }

        let statementAmount = record.statementAmount
        statementAmount = parseFloat(statementAmount)

        let transactions = _.clone(this.transactions)

        transactions = this.$store.getters['paymentReconciliation/batch/transactionsByGroup'](transactions, group)
        transactions = this.$store.getters['paymentReconciliation/batch/transactionsByMethodType'](transactions, 'cc')

        let ccAmount = this.sumOfTransactions(transactions)
        ccAmount     = parseFloat(ccAmount)

        if (!statementAmount || !statementAmount) {
          return false
        }

        return (statementAmount ? statementAmount.toFixed(2) : 0) === (ccAmount ? ccAmount.toFixed(2) : 0)
      },
      collapseRow(row) {
        if (!row.is_collapsible) {
          return
        }
        if (_.includes(this.collapsedRowIds, row.id)) {
          return
        }

        this.collapsedRowIds.push(row.id)
        this.storeCollapsedRows()
      },
      expandRow(row) {
        if (!row.is_collapsible) {
          return
        }
        if (!_.includes(this.collapsedRowIds, row.id)) {
          return
        }

        this.collapsedRowIds = _.without(this.collapsedRowIds, row.id)
        this.storeCollapsedRows()
      },
      isRowInCollapsedGroup(row) {
        if (row.is_collapsible) {
          return false
        }

        return !!_
          .chain(this.collapsedRows)
          .find((collapsedRow) => {
            return _.includes(collapsedRow.collapse_group, row.id)
          })
          .value()
      },
      isRowCollapsed(row) {
        return row.is_collapsible && _.includes(this.collapsedRowIds, row.id)
      },
      pendingGroup(group) {
        return _.get(this.pending, [group])
      },
      pendingGroupProvider(group) {
        return _.get(this.pending, [group, 'provider'])
      },
      pendingGroupStatementAmount(group) {
        return _.get(this.pending, [group, 'statementAmount'], 0)
      },
      pendingGroupLastModification(group) {
        return _.get(this.pending, [group, 'lastModification'])
      },
      storeVisibility() {
        localStorage.setItem(VISIBILITY_STORAGE_NAME, this.visible)
      },
      restoreVisibility() {
        this.visible = JSON.parse(localStorage.getItem(VISIBILITY_STORAGE_NAME))
          || this.visible
      },
      storeCollapsedRows() {
        this.LS_storageSaveItem(COLLAPSED_ROWS_STORAGE_NAME, this.collapsedRowIds)
      },
      restoreCollapsedRows() {
        this.collapsedRowIds = _
          .chain(COLLAPSED_ROWS_STORAGE_NAME)
          .thru((name) => {
            return this.LS_storageRestoreItem(name, [])
          })
          .filter((entry) => {
            return 'string' === typeof entry
          })
          .uniq()
          .value()
      },
    },
    components: {
      Money
    },
    directives: {
      tooltip: Tooltip,
      'number-format': NumberFormat
    },
    filters: {
      toUpper,
      format
    },
    mixins: [
      BatchMixin,
      MixinLocalStorage
    ]
  }
</script>
